import { ServiceCaller } from "../base";
import { AnyPlainObject } from "../../common/misc";

const parseJsonOrThrow = async (res: Response) => {
  try {
    return JSON.parse(await res.text());
  } catch {
    throw new Error("Unexpected response from server");
  }
};

const handleServiceErrors = async (promise: Promise<Response>) => {
  let res;
  try {
    // try to get result from promise
    res = await promise;
  } catch {
    // no response due to network errors
    throw new Error("No response from server");
  }
  if (!res.ok) {
    // server returned a response but error code is not ok
    // let it throw if it's not valid JSON
    const errJson = await parseJsonOrThrow(res);
    if (errJson.err) {
      // throw error with message from server if "err" is set
      // a service is expected to throw a human-readable message
      throw Error(errJson.err);
    } else {
      // otherwise tell user it's an unspecified error
      throw Error(
        `Server returned an error and it was not clear why. Message: ${JSON.stringify(
          errJson
        )}`
      );
    }
  } else {
    return res;
  }
};

export type WebRosProxyServiceOptions = {
  serviceUrl: string;
  authHeader?: string;
};

export class WebRosProxyServiceCaller implements ServiceCaller {
  serviceUrl: string;
  authHeader: string;
  constructor({ serviceUrl, authHeader = "" }: WebRosProxyServiceOptions) {
    this.serviceUrl = serviceUrl;
    this.authHeader = authHeader;
  }

  private makeServiceRequest(body: AnyPlainObject) {
    const promise = fetch(this.serviceUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    });
    return handleServiceErrors(promise).then((res) => parseJsonOrThrow(res));
  }

  callService(service: string, data: any, options?: { serviceType?: string }) {
    return this.makeServiceRequest({
      service,
      cmd: data,
    });
  }

  callSpotService(cmd: string, data: any) {
    return this.makeServiceRequest({
      service: "spot",
      cmd,
      data,
    });
  }

  callCustomActionService(service: string, args: AnyPlainObject) {
    return this.makeServiceRequest({
      service,
      ...args,
    });
  }
}
